/* eslint-disable @typescript-eslint/no-explicit-any */ // TODO: Remove this disable and fix all any types in this file
import { SearchContext, SearchField } from "@/shared/search/search.types";
import { Facet, FacetSelected } from "@/shared/facets/facets.model";
import {
    AssetFilter,
    ContentFilter,
    DateRange,
    FieldDefinitionTypes,
} from "@/shared/services/graphql/generated/consumer-graph-types";
import { generateSearchFieldsFilterClause } from "@/shared/search/search-fields-filter-clause";
import {
    BTW_CHAR,
    convertToUTC,
    dayjs,
    GTE_CHAR,
    LTE_CHAR,
    URL_PARAM_DATE_FORMAT,
    VALID_DATE_LENGTH,
} from "@/shared/utils/date-utils.ts";

type QueryFilter =
    | { taxonomy: Record<string, string | boolean> }
    | { equals: Record<string, string | boolean> }
    | { andGroup: { range: Record<string, DateRange> }[] };

export type OrGroupQuery = Array<{ orGroup: QueryFilter[] }>;

export const generateOrGroupsByFacetsSelected = (selected: FacetSelected[], currentFacet?: Facet): OrGroupQuery => {
    //remove own facet from selected
    const copySelected = selected.filter((x) => x.referencedId !== currentFacet?.referencedId);

    const generateOrQueryParts = (val: FacetSelected) => {
        return val.values.map((value) => {
            const filterValue = val.type === FieldDefinitionTypes.boolean ? value === "true" : value;
            const filters: Record<string, string | boolean> = { [val.referencedId]: filterValue };

            if (val.type === FieldDefinitionTypes.taxonomy) {
                return { taxonomy: filters };
            }

            return { equals: filters };
        });
    };

    const generateFilterPartsForDateFacets = (val: FacetSelected) => {
        return val.values.flatMap((value): any => {
            const dateRange = parseDateRange(value);

            if (!dateRange) return undefined;

            return {
                andGroup: dateRange.map((date) => {
                    return { range: { [val.referencedId]: date } };
                }),
            };
        });
    };

    const selectedWithValues = copySelected.length > 0 ? copySelected.filter((x) => x.values.length > 0) : [];

    return selectedWithValues.map((x) => ({
        orGroup:
            x.type === FieldDefinitionTypes.dateTime ? generateFilterPartsForDateFacets(x) : generateOrQueryParts(x),
    }));
};

export const generateQueryFilterForSearch = (
    selectedFacets: FacetSelected[],
    fuzzy: boolean,
    searchContext?: SearchContext,
    searchFields?: SearchField[],
    searchPhrase?: string,
    currentFacet?: Facet
): {
    contentFilter: ContentFilter;
    assetFilter: AssetFilter | undefined;
    productId: string | undefined;
    mechanicId: string | undefined;
    articleId: string | undefined;
} => {
    const contentFilter: ContentFilter = {};
    let assetFilter: AssetFilter | undefined;
    let productId: string | undefined;
    let mechanicId: string | undefined;
    let articleId: string | undefined;

    if (searchContext?.mechanic) {
        mechanicId = searchContext.mechanic;
    } else if (searchContext?.product && searchContext?.asset) {
        assetFilter = { equals: { productId: searchContext.product, assetId: searchContext.asset } };
    } else if (searchContext?.product) {
        productId = searchContext.product;
    } else if (searchContext?.article) {
        articleId = searchContext.article;
    }

    if (searchPhrase) {
        //get filter clause for search fields and add to content filter
        const searchFilterClause = generateSearchFieldsFilterClause(searchPhrase, searchFields ?? [], fuzzy);
        contentFilter.orGroup = searchFilterClause.orGroup;
    }

    //get filter clause for selected facets and add to content filter
    const orGroups = generateOrGroupsByFacetsSelected(selectedFacets, currentFacet);
    if (orGroups.length > 0) contentFilter.andGroup = orGroups;

    return { contentFilter, assetFilter, productId, mechanicId, articleId };
};

export const parseDateRange = (param: string | null): DateRange[] | null => {
    if (!param) return null;

    if (param.startsWith(GTE_CHAR)) {
        return [
            {
                gte: {
                    date: dayjs(convertToUTC(param.substring(1), URL_PARAM_DATE_FORMAT))
                        .startOf("day")
                        .toISOString(),
                },
            },
        ];
    }
    if (param.startsWith(LTE_CHAR)) {
        return [
            {
                lte: {
                    date: dayjs(convertToUTC(param.substring(1), URL_PARAM_DATE_FORMAT))
                        .endOf("day")
                        .toISOString(),
                },
            },
        ];
    }
    if (param.includes(BTW_CHAR)) {
        const [from, to] = param.split(BTW_CHAR).map((dateStr) => dateStr.trim());

        return [
            { gte: { date: dayjs(convertToUTC(from, URL_PARAM_DATE_FORMAT)).startOf("day").toISOString() } },
            { lte: { date: dayjs(convertToUTC(to, URL_PARAM_DATE_FORMAT)).endOf("day").toISOString() } },
        ];
    } else if (param.length === VALID_DATE_LENGTH) {
        return [
            { gte: { date: dayjs(convertToUTC(param, URL_PARAM_DATE_FORMAT)).startOf("day").toISOString() } },
            { lte: { date: dayjs(convertToUTC(param, URL_PARAM_DATE_FORMAT)).endOf("day").toISOString() } },
        ];
    }

    return null;
};
